import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { CartSelection } from 'public/components/online_ordering/types'

import { useModal } from 'src/shared/components/common/modal/useModal'
import ItemModal from '../item_modal/ItemModal'
import {
  useMenuItemDetailsQuery,
  useMenuItemFromCartQuery
} from 'src/apollo/onlineOrdering'
import { useOOClient } from 'shared/components/common/oo_client_provider/OOClientProvider'
import { useFulfillment } from 'src/public/components/online_ordering/FulfillmentContext'
import { useOptionalTimeBasedRules } from 'src/public/components/online_ordering/TimeBasedRuleContext'
import { getTBRContent } from '../item_tags/MenuItemTags'
import { useRestaurant } from 'shared/components/common/restaurant_context/RestaurantContext'
import { PlaceSettingIcon } from '@toasttab/buffet-pui-icons'
import FormattedPrice from 'src/shared/components/common/price/FormattedPrice'
import {
  flattenedModifiersToString,
  flattenModifiers
} from '../order/OrderItemsUtil'
import classnames from 'classnames'
import { t } from 'src/translations/src'
import QuantitySelector from '../item_modal/QuantitySelector'
import { useCart } from 'src/public/components/online_ordering/CartContext'
import Skeleton from 'react-loading-skeleton'
import { debounce } from 'lodash'
import {
  AlertModalSource,
  useAlertModalContext
} from 'src/shared/components/common/alert_modal/AlertModal'
import { EditSource, ItemSource } from './cartUtils'
import {
  markSelectedModifiers,
  collapseModifier
} from 'src/public/components/online_ordering/modifierUtils'
import { reportError } from 'src/lib/js/clientError'
import { useA11y } from 'src/public/components/online_ordering/useA11y'
import {
  roundPrice,
  useFormatPriceCallback
} from 'src/lib/js/hooks/useFormatPrice'
import { Channel } from 'src/public/js/siteUtilities'

import { ShippingValidationProblem } from './Cart'

type CartItemV2Props = {
  selection: CartSelection
  itemSource: ItemSource
  hasInvalidContent?: (
    selection: CartSelection
  ) => ShippingValidationProblem | undefined
}

const CartItemV2 = ({
  selection,
  itemSource,
  hasInvalidContent
}: CartItemV2Props) => {
  const { isOpen, onClose, onOpen } = useModal()
  const validationProblem = hasInvalidContent?.(selection)
  const { channel } = useCart()
  const { selectedLocation } = useRestaurant()
  const channelGuid =
    channel === Channel.ECOMM
      ? selectedLocation?.ecommMenuChannelGuid ?? undefined
      : undefined

  if (!selection?.guid || !selection?.itemGuid) {
    return null
  }
  return (
    <>
      <ItemModal
        isOpen={isOpen}
        onClose={onClose}
        itemGuid={selection.itemGuid}
        itemGroupGuid={selection.itemGroupGuid}
        selectionGuid={selection.guid}
        itemSource={itemSource}
        channelGuid={channelGuid}
      />
      <OrderItemV2
        selection={selection}
        onOpenItemModal={onOpen}
        validationProblem={validationProblem}
        channelGuid={channelGuid}
      />
    </>
  )
}

type OrderItemV2Props = {
  selection: CartSelection
  onOpenItemModal: () => void
  validationProblem?: ShippingValidationProblem
  channelGuid?: string
}

const OrderItemV2 = ({
  selection,
  onOpenItemModal,
  validationProblem,
  channelGuid
}: OrderItemV2Props) => {
  const ooClient = useOOClient()
  const { fulfillmentData } = useFulfillment()
  const { ooRestaurant } = useRestaurant()
  const { timeBasedRulesMap } = useOptionalTimeBasedRules() || {
    timeBasedRulesMap: {} as NonNullable<
      ReturnType<typeof useOptionalTimeBasedRules>
    >['timeBasedRulesMap']
  }
  const timeBasedRules =
    selection?.itemGuid && timeBasedRulesMap[selection.itemGuid]
  const timeBasedRuleText = timeBasedRules
    ? getTBRContent(timeBasedRules, true, fulfillmentData)
    : ''
  const { loadingCart, cartGuid, channel, deleteFromCart, editCartItem } =
    useCart()
  const { openAlertModal } = useAlertModalContext()
  const { announce } = useA11y()
  const [isBeingDeleted, setIsBeingDeleted] = useState(false)
  const cartItemRef = useRef<HTMLDivElement>(null)
  const capturedHeightRef = useRef<number | null>(null)

  // Query for full menu item details including image
  const { data: menuItemData, loading: loadingDetails } =
    useMenuItemDetailsQuery({
      variables: {
        input: {
          itemGuid: selection?.itemGuid || '',
          itemGroupGuid: selection?.itemGroupGuid || '',
          restaurantGuid: ooRestaurant?.guid || '',
          dateTime: fulfillmentData?.cartFulfillmentData?.fulfillmentDateTime,
          channelGuid,
          includeSlugs: channel === Channel.ECOMM
        },
        nestingLevel: 10
      },
      client: ooClient,
      fetchPolicy: 'cache-first'
    })

  // Query for cart selection with modifiers (needed for editing)
  const { data: cartSelectionData, loading: loadingCartSelection } =
    useMenuItemFromCartQuery({
      variables: {
        input: {
          itemGuid: selection?.itemGuid || '',
          itemGroupGuid: selection?.itemGroupGuid || '',
          restaurantGuid: ooRestaurant?.guid || '',
          selectionGuid: selection?.guid || '',
          cartGuid: cartGuid || '',
          dateTime: fulfillmentData?.cartFulfillmentData?.fulfillmentDateTime,
          channelGuid
        },
        nestingLevel: 10
      },
      client: ooClient,
      fetchPolicy: 'cache-first'
    })

  const isLoading = loadingCart || loadingDetails || loadingCartSelection

  // Capture height before transitioning to loading state
  useLayoutEffect(() => {
    if (!isLoading && cartItemRef.current) {
      capturedHeightRef.current = cartItemRef.current.offsetHeight
    }
  })

  const menuItemDetails = menuItemData?.menuItemDetails

  const specialInstructions = useMemo(() => {
    return selection?.modifiers?.find((m) => m?.name && !m?.optionGroupGuid)
      ?.name
  }, [selection?.modifiers])

  const formatPrice = useFormatPriceCallback()

  const updateCartQuantity = useCallback(
    async (newQuantity: number) => {
      if (!selection?.guid) return

      try {
        if (newQuantity === 0) {
          setIsBeingDeleted(true)
          await deleteFromCart(selection.guid)
          announce(
            t('cart-item.removed-from-cart', {
              itemName: selection.name || t('cart-item.item')
            })
          )
        } else {
          // Use cart selection data to preserve modifiers
          if (!cartSelectionData?.selectionItemDetails)
            throw new Error('Selection item details not found')

          // Mark modifiers as selected based on what's in the cart
          const modifierGroupsWithSelection = markSelectedModifiers(
            (selection.modifiers || []).filter(
              (m): m is NonNullable<typeof m> => m !== null
            ),
            cartSelectionData.selectionItemDetails.modifierGroups || []
          )

          const wrappedSelection = {
            ...collapseModifier({
              ...cartSelectionData.selectionItemDetails,
              price: cartSelectionData.selectionItemDetails.menuItemPrice,
              modifierGroups: modifierGroupsWithSelection
            }),
            specialInstructions,
            usesFractionalQuantity:
              cartSelectionData.selectionItemDetails.usesFractionalQuantity,
            unitOfMeasure:
              cartSelectionData.selectionItemDetails.fractionalQuantity
                ?.unitOfMeasure
          }

          await editCartItem(
            wrappedSelection,
            newQuantity,
            selection.guid,
            ItemSource.CartQuantitySelector,
            EditSource.CartQuantitySelector
          )
          // selection.price will be stale by the time we announce, so calculate
          // the updated price based on the unit price and newQuantity
          const unitPrice = roundPrice(
            (selection.price || 0) / (selection.quantity || 1)
          )
          const newPrice = formatPrice(roundPrice(unitPrice * newQuantity))
          announce(
            t('cart-item.quantity-updated', {
              itemName: selection.name || t('cart-item.item'),
              quantity: newQuantity,
              price: newPrice
            })
          )
        }
      } catch (error) {
        reportError('Error updating item quantity in cart:', error)
        openAlertModal(
          'alert-modal.edit-item.message',
          'alert-modal.edit-item.title',
          AlertModalSource.CART_QUANTITY_SELECTOR
        )
      }
    },
    [
      announce,
      cartSelectionData?.selectionItemDetails,
      deleteFromCart,
      editCartItem,
      formatPrice,
      openAlertModal,
      selection?.guid,
      selection?.modifiers,
      selection?.name,
      selection?.price,
      selection?.quantity,
      specialInstructions
    ]
  )

  useEffect(() => {
    if (!isLoading && isBeingDeleted) {
      setIsBeingDeleted(false)
    }
  }, [isBeingDeleted, isLoading])

  const debouncedUpdateCart = useMemo(
    () => debounce(updateCartQuantity, 1000),
    [updateCartQuantity]
  )

  useEffect(() => {
    return () => {
      debouncedUpdateCart.cancel()
    }
  }, [debouncedUpdateCart])

  const handleQuantityChange = useCallback(
    (newQuantity: number, _change: number) => {
      debouncedUpdateCart(newQuantity)
    },
    [debouncedUpdateCart]
  )

  if (!selection || isBeingDeleted) {
    return null
  }

  // Extract image and description from query result
  const imageUrls = menuItemDetails?.imageUrls
  const image = imageUrls?.medium || imageUrls?.small || null

  // Occasionally prices come back with floating-point weirdness, e.g. 42.449999999999996.
  // First round each price before performing the comparison.
  const isItemDiscounted =
    roundPrice(selection.price || 0) <
    roundPrice(selection.preDiscountPrice || 0)
  const discountedPrice =
    selection.price === 0 ? (
      t('cart-item.free')
    ) : (
      <FormattedPrice value={selection.price || 0} />
    )

  const cleanedModifiers = selection.modifiers?.filter(
    (m) => m?.name && m?.optionGroupGuid
  )
  const flattenedModifiers = cleanedModifiers
    ? flattenModifiers(cleanedModifiers)
    : []
  const modifiersString = flattenedModifiersToString(flattenedModifiers)

  return (
    <div
      className={classnames('itemV2', { isLoading })}
      ref={cartItemRef}
      style={
        isLoading && capturedHeightRef.current
          ? { minHeight: `${capturedHeightRef.current}px` }
          : undefined
      }
    >
      <div className='itemV2Row'>
        <button
          disabled={isLoading}
          onClick={onOpenItemModal}
          data-testid='cart-item-v2'
          className={classnames('itemV2Button', { isLoading })}
          type='button' // so as not to trigger the checkout form submit
        >
          {isLoading ? (
            <CartItemSkeleton
              hasDescription={!!modifiersString}
              hasSpecialInstructions={
                !!(specialInstructions ?? timeBasedRuleText)
              }
            />
          ) : (
            <>
              <div className='imageContainer'>
                <CartItemImage src={image} alt={selection.name} />
              </div>
              <div className='details'>
                <div className='textContainer'>
                  <h2 className='name'>{selection.name}</h2>
                  {modifiersString ? (
                    <p className='description'>{modifiersString}</p>
                  ) : null}
                  {timeBasedRuleText ? (
                    <p className='description'>{timeBasedRuleText}</p>
                  ) : null}
                  {specialInstructions && (
                    <p className='description'>{specialInstructions}</p>
                  )}
                </div>
                <div className='priceContainer'>
                  {isItemDiscounted && (
                    <span className='price' data-testid='discounted-price'>
                      {discountedPrice}
                    </span>
                  )}
                  <span
                    data-testid='price'
                    className={classnames('price', {
                      discountedPrice: isItemDiscounted
                    })}
                  >
                    <FormattedPrice value={selection.preDiscountPrice || 0} />
                  </span>
                </div>
              </div>
            </>
          )}
        </button>
        {!isLoading && (
          <QuantitySelector
            min={selection.usesFractionalQuantity ? 0 : 1}
            canIncrease={true}
            allowDelete={true}
            className='ooQuantitySelector'
            defaultValue={
              selection.usesFractionalQuantity
                ? selection.fractionalQuantity.quantity
                : selection.quantity || 1
            }
            onQuantityChange={handleQuantityChange}
            onDelete={() => handleQuantityChange(0, 0)}
            totalItemPrice={selection.price || 0}
            usesFractionalQuantity={!!selection.usesFractionalQuantity}
            disableAnnouncement
          />
        )}
      </div>
      {validationProblem && (
        <div className='validationError itemV2-validationError' role='alert'>
          {validationProblem.code === 'INVALID_FULFILLMENT'
            ? t('order-section.item-shipping-exclusion')
            : t('order-section.item-cannot-be-shipped')}
        </div>
      )}
    </div>
  )
}

const CartItemSkeleton = ({
  hasDescription,
  hasSpecialInstructions
}: {
  hasDescription: boolean
  hasSpecialInstructions: boolean
}) => {
  return (
    <>
      <div className='imageContainer'>
        <div className='itemImagePlaceholder cartItemImage'>
          <Skeleton height='100%' width='100%' />
        </div>
      </div>
      <div className='details'>
        <div className='textContainer'>
          <h2 className='name'>
            <Skeleton
              height='16px'
              width='80%'
              containerTestId='name-skeleton'
              containerClassName='skeleton'
            />
          </h2>
          {hasDescription && (
            <p className='description'>
              <Skeleton
                height='10px'
                width='70%'
                containerClassName='skeleton'
                containerTestId='modifiers-skeleton'
              />
            </p>
          )}
          {hasSpecialInstructions && (
            <p className='description'>
              <Skeleton
                height='10px'
                width='50%'
                containerClassName='skeleton'
                containerTestId='special-instructions-skeleton'
              />
            </p>
          )}
        </div>
        <div className='priceContainer loading'>
          <Skeleton
            height='12px'
            width='20%'
            containerTestId='description-skeleton'
            containerClassName='skeleton'
          />
        </div>
      </div>
    </>
  )
}

const CartItemImage = ({
  src,
  alt
}: {
  src: string | null
  alt?: string | null
}) => {
  if (!src) {
    return (
      <div
        className='itemImagePlaceholder cartItemImage'
        data-testid='placeholder-image'
      >
        <PlaceSettingIcon accessibility='decorative' />
      </div>
    )
  }
  return (
    <>
      <div className='imageGradient' />
      <img src={src} alt={alt || ''} className='cartItemImage' />
    </>
  )
}

export default CartItemV2
